import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Icon } from '@iconify/react';
import { createUserWithEmailAndPassword, signInWithPopup } from 'firebase/auth';
import { auth, googleProvider } from '../../firebase'; // adjust path
import { useLanguage } from '../../i18n';
import styles from './Register.module.css'; // reuse same styles or copy from Login

export default function Register() {
  const { t } = useLanguage();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleEmailSignUp = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      setLoading(false);
      return;
    }

    try {
      await createUserWithEmailAndPassword(auth, email, password);
      navigate('/'); // redirect to home
    } catch (err) {
      setError(err.message || 'Sign up failed');
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSignUp = async () => {
    setError('');
    setLoading(true);

    try {
      await signInWithPopup(auth, googleProvider);
      navigate('/'); // redirect to home
    } catch (err) {
      setError(err.message || 'Google sign up failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <main className={styles.main}>
      <div className={styles.container}>
        <div className={styles.card}>
          <div className={styles.logoWrapper}>
            <Icon icon="fa-solid:shield-alt" className={styles.logo} />
            <h1 className={styles.title}>{t('registerTitle')}</h1>
          </div>

          <p className={styles.subtitle}>
            {t('registerSubtitle')}
          </p>

          {error && (
            <div className={styles.error}>
              <Icon icon="lucide:alert-triangle" />
              {error}
            </div>
          )}

          <form onSubmit={handleEmailSignUp} className={styles.form}>
            <div className={styles.field}>
              <label htmlFor="email">{t('emailLabel')}</label>
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="sarah.chen@securenet.org"
                required
                disabled={loading}
              />
            </div>

            <div className={styles.field}>
              <label htmlFor="password">{t('passwordLabel')}</label>
              <input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                required
                disabled={loading}
              />
            </div>

            <div className={styles.field}>
              <label htmlFor="confirmPassword">{t('confirmPasswordLabel')}</label>
              <input
                id="confirmPassword"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="••••••••"
                required
                disabled={loading}
              />
            </div>

            <button type="submit" className={styles.btnPrimary} disabled={loading}>
              {loading ? t('signingUp') : t('registerTitle')}
            </button>
          </form>

          <div className={styles.divider}>
            <span>{t('orText')}</span>
          </div>

          <button 
            className={styles.socialBtn} 
            onClick={handleGoogleSignUp}
            disabled={loading}
          >
            <Icon icon="logos:google-icon" className={styles.socialIcon} />
            {t('continueWithGoogle')}
          </button>

          <div className={styles.footerLinks}>
            <p>
              {t('alreadyHaveAccount')}{' '}
              <Link to="/login" className={styles.link}>
                {t('loginTitle')}
              </Link>
            </p>
          </div>
        </div>
      </div>
    </main>
  );
}